import {del, get, set} from 'object-path-immutable'
import {isDescendant} from '../../../../../common/action-flows/config/step-traversal'
import {
  ActionElement,
  CardDefinition,
  ContentElement,
  ContextMappingValue,
  InputElement,
  UIElement
} from '../../../../../common/types'
import {ActionFlowStepCodec} from '../../../../../common/types/action-flows'
import {
  ACTION_FLOW_START_STEP_ID,
  defaultCardActions
} from '../../../../../common/types/constants'
import {
  renameTemplateVariableRefs,
  renameValueVariableRefs
} from '../../../../../common/variable-engine'
import {
  newBannerElement,
  newUIElement
} from '../../../../../lib/card-element-helpers'
import {copyName} from '../../../../../lib/copy-name'
import {omit} from '../../../../../lib/updaters'
import {
  Subview,
  deepClone,
  getElementPath,
  merge,
  updateCardElementAttributes,
  updateCardElementValidations,
  updateCardSubview,
  updateCardTemplate,
  updateConfig,
  updateRequestListener,
  updateStep,
  updateTrigger
} from '../../helpers'
import {FlowAction} from './actions'
import {getCardCount} from './selectors'
import {ActionFlowState} from './state'

const overwrite = <E extends UIElement>(
  current: E['attributes'],
  valuesToSet: Partial<E['attributes']>
) => {
  return {
    ...current,
    ...valuesToSet
  }
}

export const flowReducer = (prevState: ActionFlowState, action: FlowAction) => {
  switch (action.type) {
    case 'update-step-properties':
      return updateStep(prevState, action.stepId, prevStep => {
        const step = action.step
        const updated = set(prevStep, 'properties', step.properties)
        if (!ActionFlowStepCodec.is(updated)) {
          console.error(
            'Step is invalid after update, ignoring action',
            action,
            updated
          )
          return prevStep
        }
        return updated
      })

    case 'set-step-output-properties':
      return updateStep(prevState, action.stepId, prevStep => {
        const updated = {
          ...prevStep,
          properties: {
            ...prevStep.properties,
            output: {
              ...get(prevStep, 'properties.output', {}),
              ...action.outputProperties
            }
          }
        }
        if (!ActionFlowStepCodec.is(updated)) {
          console.error(
            'Step is invalid after update, ignoring action',
            action,
            updated
          )
          return prevStep
        }
        return updated
      })

    case 'update-step-name':
      return updateStep(prevState, action.stepId, prevStep =>
        set(prevStep, 'name', action.name)
      )

    // TODO unit tests for this one
    case 'delete-step':
      return updateConfig(prevState, config => {
        const step = config.steps[action.stepId]

        if (!step) {
          // step is already gone, so no-op
          return config
        }

        // delete the step
        let updated = del(config, `steps.${action.stepId}`)

        // replace any links to the deleted step with links to the deleted
        // step's next steps...
        for (const otherStepId of Object.keys(updated.steps)) {
          const otherStep = updated.steps[otherStepId]

          if (otherStep.next?.includes(action.stepId)) {
            updated = set(
              updated,
              `steps.${otherStepId}.next`,
              (updated.steps[otherStepId].next ?? [])
                .filter(id => id !== action.stepId)
                .concat(
                  (step.next ?? []).filter(
                    // ...unless they are already descendants
                    id => !isDescendant(updated.steps, otherStepId, id)
                  )
                )
            )

            // clear deleting step id from conditional step
            const conditionalStep = updated.steps[otherStepId] // to get the updated conditional step from above
            if (
              conditionalStep.type === 'condition' &&
              (conditionalStep.properties.cases.some(c =>
                c.nextStepIds?.includes(action.stepId)
              ) ||
                conditionalStep.properties.defaultNextStepIds?.some(
                  c => c === action.stepId
                ))
            ) {
              updated = set(updated, `steps.${otherStepId}`, {
                ...conditionalStep,
                properties: {
                  ...conditionalStep.properties,
                  cases: conditionalStep.properties.cases.map(c => {
                    if (c.nextStepIds?.includes(action.stepId)) {
                      return {
                        ...c,
                        nextStepIds: c.nextStepIds.filter(
                          s => s !== action.stepId
                        )
                      }
                    }
                    return c
                  }),
                  defaultNextStepIds:
                    conditionalStep.properties.defaultNextStepIds?.filter(
                      s => s !== action.stepId
                    )
                }
              })
            }
          }
        }
        return updated
      })

    // TODO unit tests for this one
    case 'add-step':
    case 'add-create-card-step': {
      // create card step is much like add step with a few extra operations
      const updatedConfig = updateConfig(prevState, config => {
        if (action.step.id in config.steps) {
          // TODO do we need to handle this?
          throw new Error('Clashing step id')
        }

        let updated = set(config, `steps.${action.step.id}`, action.step)

        const previous =
          config.steps[action.previousStepId ?? ACTION_FLOW_START_STEP_ID]

        // insert between previous and next steps (if provided)
        updated = set(
          updated,
          `steps.${previous.id}.next`,
          (previous.next ?? [])
            .filter(id => !action.step.next?.includes(id))
            .concat([action.step.id])
        )

        // insert as default step if default doesn't exist in conditional step
        if (
          previous.type === 'condition' &&
          (!previous.properties.defaultNextStepIds ||
            !previous.properties.defaultNextStepIds.length)
        ) {
          updated = set(
            updated,
            `steps.${previous.id}.properties.defaultNextStepIds`,
            [action.step.id]
          )
        }

        if (action.type === 'add-step') {
          // todo should be via a different action.type not part of 'add-step'
          // as this isn't relevant to most steps
          // see how we have a separate type for add-create-card-step
          updated = action.samplePayload
            ? {
                ...updated,
                metadata: {
                  ...updated.metadata,
                  payloadSamples: {
                    connector: {},
                    ...updated.metadata?.payloadSamples,
                    step: {
                      ...updated.metadata?.payloadSamples.step,
                      [action.step.id]: {sample: action.samplePayload}
                    }
                  }
                }
              }
            : updated
        }

        if (action.type === 'add-create-card-step') {
          updated = set(
            updated,
            `steps.${action.step.id}.properties.cardTemplateId`,
            action.cardTemplateId
          )
          updated = set(
            updated,
            `steps.${action.step.id}.name`,
            action.cardName ?? `Create card ${getCardCount(updated) + 1}`
          )
        }

        return updated
      })

      // we are still in the case block here, but want to update the config again
      // when handling this action, as we also need to create the card template
      if (action.type === 'add-create-card-step') {
        return updateCardTemplate(
          updatedConfig,
          action.cardTemplateId,
          emptyDef => ({
            ...emptyDef,
            metadata: {
              ...emptyDef.metadata,
              cardName:
                action.cardName ??
                `Card template ${getCardCount(updatedConfig.config) + 1}`
            }
          })
        )
      }
      return updatedConfig
    }

    case 'disconnect-steps': {
      if (
        !(action.stepId in prevState.config.steps) ||
        !(action.nextStepId in prevState.config.steps)
      ) {
        return prevState
      }
      let updated = updateConfig(prevState, config => {
        const parent = config.steps[action.stepId]

        return set(
          config,
          `steps.${action.stepId}.next`,
          parent.next?.filter(s => s !== action.nextStepId)
        )
      })

      // Now clean up disconnected steps so that they no longer
      // reference the parent.  TODO needs some thought
      const nextStep = updated.config.steps[action.nextStepId]

      if (
        nextStep.type === 'event-listener' &&
        nextStep.properties.condition.type === 'card'
      ) {
        updated = updateStep(updated, action.nextStepId, cardListenerStep => {
          const sourceStepIds = get(
            cardListenerStep,
            'properties.condition.sourceStepIds',
            []
          )
          return set(
            cardListenerStep,
            'properties.condition.sourceStepIds',
            sourceStepIds.filter(s => s !== action.stepId)
          )
        })
      }

      return updated
    }

    // TODO unit tests for this one
    case 'connect-steps': {
      if (
        !(action.stepId in prevState.config.steps) ||
        !(action.nextStepId in prevState.config.steps)
      ) {
        return prevState
      }

      let updated = updateConfig(prevState, config => {
        const next = config.steps[action.stepId].next ?? []

        // check for legality of connection
        if (
          // can't connect to self
          action.stepId === action.nextStepId ||
          // already connected
          action.nextStepId in next ||
          // next is already descended from step
          isDescendant(config.steps, action.stepId, action.nextStepId) ||
          // step is already descended from next (no cycles)
          isDescendant(config.steps, action.nextStepId, action.stepId)
        ) {
          return config
        }

        return set(
          config,
          `steps.${action.stepId}.next`,
          next.concat([action.nextStepId])
        )
      })

      // Set additional properties based on the edge connection.
      // i.e. setting sourceStepIds.  Todo needs some more thought here
      // maybe we want an on connect / on disconnect interface for steps 🤔
      const nextStep = updated.config.steps[action.nextStepId]
      if (
        nextStep.type === 'event-listener' &&
        nextStep.properties.condition.type === 'card'
      ) {
        updated = updateStep(updated, action.nextStepId, cardListenerStep => {
          const sourceStepIds = get(
            cardListenerStep,
            'properties.condition.sourceStepIds',
            []
          )
          return set(cardListenerStep, 'properties.condition.sourceStepIds', [
            ...sourceStepIds,
            action.stepId
          ])
        })
      }

      return updated
    }

    // TODO unit tests for this one
    case 'add-trigger':
      return updateConfig(prevState, config => {
        const updated = set(
          config,
          `triggers.${action.trigger.id}`,
          action.trigger
        )

        return updated
      })

    // TODO unit tests for this one
    case 'delete-trigger':
      return updateConfig(prevState, config => {
        const trigger = config.triggers?.[action.triggerId]

        if (!trigger) {
          // step is already gone, so no-op
          return config
        }

        // delete the trigger
        let updated = del(config, `triggers.${action.triggerId}`)

        if (trigger.type === 'connector') {
          updated = del(updated, `connectors.${trigger.properties.connectorId}`)
        }

        return updated
      })

    case 'update-trigger-name':
      return updateTrigger(prevState, action.triggerId, prevTrigger =>
        set(prevTrigger, 'name', action.name)
      )

    case 'update-trigger-properties':
      return updateTrigger(prevState, action.triggerId, prevTrigger => {
        return set(
          prevTrigger,
          'properties',
          merge(prevTrigger.properties, action.properties)
        )
      })

    case 'set-name':
      return {
        ...prevState,
        name: action.name
      }

    case 'variable-update':
      return updateConfig(prevState, config => {
        return {
          ...config,
          variables: {
            ...config.variables,
            [action.name]: action.variable
          }
        }
      })

    case 'variable-rename':
      return updateConfig(prevState, config => {
        if (!config.variables) {
          return config
        }
        // rename instances of this variable throughout templates
        const templatesVarRenamed = renameTemplateVariableRefs(
          action.from,
          action.to,
          config.cardTemplates ?? {}
        )
        // rename instances of this variable within other variables
        const variablesVarRenamed = renameValueVariableRefs(
          action.from,
          action.to,
          config.variables
        )
        return {
          ...config,
          cardTemplates: templatesVarRenamed,
          variables: {
            // now move from old name to new name
            ...omit(variablesVarRenamed, action.from),
            [action.to]: variablesVarRenamed[action.from]
          }
        }
      })

    case 'variable-delete':
      return updateConfig(prevState, config => {
        return {
          ...config,
          variables: config.variables
            ? omit(config.variables, action.name)
            : undefined
        }
      })

    case 'add-card-banner':
      return updateCardTemplate(prevState, action.templateId, current =>
        set(
          current,
          'metadata.banner',
          newBannerElement(action.bannerType, action.key)
        )
      )

    case 'delete-card-banner':
      return updateCardTemplate(prevState, action.templateId, current =>
        del(current, 'metadata.banner')
      )

    case 'update-card-banner-attributes':
      return updateCardTemplate(prevState, action.templateId, current =>
        set(
          current,
          'metadata.banner.attributes',
          merge(
            get(current, 'metadata.banner.attributes', {}),
            action.attributes
          )
        )
      )

    case 'overwrite-card-banner-attributes':
      return updateCardTemplate(prevState, action.templateId, current =>
        set(
          current,
          'metadata.banner.attributes',
          overwrite(
            get(current, 'metadata.banner.attributes', {}),
            action.attributes
          )
        )
      )

    case 'update-card-description':
      return updateCardTemplate(prevState, action.templateId, current =>
        action.cardDescription === undefined
          ? del(current, 'metadata.cardDescription')
          : set(current, 'metadata.cardDescription', action.cardDescription)
      )

    case 'update-card-description-icons':
      return updateCardTemplate(prevState, action.templateId, current => ({
        ...current,
        metadata: {
          ...current.metadata,
          cardDescriptionIcon: action.cardDescriptionIcon,
          cardDescriptionIconUrl: action.cardDescriptionIconUrl
        }
      }))

    case 'update-card-metadata':
      return updateCardTemplate(prevState, action.templateId, current => ({
        ...current,
        metadata: action.metadata
      }))

    case 'update-card-content':
      return updateCardTemplate(prevState, action.templateId, current => ({
        ...current,
        defaultView: {
          ...(current.defaultView ? current.defaultView : {actions: []}),
          content: action.content
        }
      }))

    case 'update-card-definition':
      // In use by the raw card editor json field
      return updateCardTemplate(
        prevState,
        action.templateId,
        () => action.definition
      )

    case 'update-card-actions':
      return updateCardTemplate(prevState, action.templateId, current => ({
        ...current,
        defaultView: {
          ...(current.defaultView ? current.defaultView : {content: []}),
          actions: action.actions
        }
      }))

    case 'add-card-action':
      return updateCardTemplate(prevState, action.templateId, current => {
        const currentActions = get<CardDefinition, UIElement[]>(
          current,
          'defaultView.actions',
          []
        )
        return set(
          current,
          'defaultView.actions',
          currentActions.concat(newUIElement(action.actionType, action.key))
        )
      })

    case 'add-card-element':
      return updateCardTemplate(prevState, action.templateId, current => {
        const currentContent = get<CardDefinition, UIElement[]>(
          current,
          'defaultView.content',
          []
        )
        return set(
          current,
          'defaultView.content',
          currentContent.concat(newUIElement(action.elementType, action.key))
        )
      })

    case 'update-card-subview-title':
      return updateCardSubview(
        prevState,
        action.templateId,
        action.subviewId,
        current => ({
          ...current,
          title: action.title
        })
      )

    case 'update-card-subview-inputs':
      return updateCardSubview(
        prevState,
        action.templateId,
        action.subviewId,
        current => ({
          ...current,
          inputs: action.inputs
        })
      )

    case 'update-card-subview-actions':
      return updateCardSubview(
        prevState,
        action.templateId,
        action.subviewId,
        current => ({
          ...current,
          actions: action.actions
        })
      )

    case 'update-card-subview':
      return updateCardSubview(
        prevState,
        action.templateId,
        action.subviewId,
        current => merge(current, action.subview) as Subview
      )

    case 'add-card-subview-action':
      return updateCardSubview(
        prevState,
        action.templateId,
        action.subviewId,
        subview => ({
          ...subview,
          actions: [
            ...subview.actions,
            newUIElement(action.actionType, action.key) as ActionElement
          ]
        })
      )

    case 'add-card-subview-element':
      return updateCardSubview(
        prevState,
        action.templateId,
        action.subviewId,
        subview => ({
          ...subview,
          inputs: [
            ...subview.inputs,
            newUIElement(action.elementType, action.key) as InputElement
          ]
        })
      )

    case 'delete-card-template':
      return updateConfig(prevState, config => {
        if (config.cardTemplates) {
          return {
            ...config,
            cardTemplates: omit(config.cardTemplates, action.templateId)
          }
        }
        return config
      })

    case 'set-card-name':
      return updateCardTemplate(prevState, action.templateId, current =>
        set(current, 'metadata.cardName', action.name)
      )

    case 'update-card-default-view-element-attributes':
      return updateCardElementAttributes<ContentElement>(
        prevState,
        {
          templateId: action.templateId,
          key: action.key,
          action: false
        },
        prevAttr => {
          return merge(
            prevAttr,
            action.attributes
          ) as ContentElement['attributes']
        }
      )

    case 'overwrite-card-default-view-element-attributes':
      return updateCardElementAttributes<ContentElement>(
        prevState,
        {
          templateId: action.templateId,
          key: action.key,
          action: false
        },
        prevAttr => {
          return overwrite(
            prevAttr,
            action.attributes
          ) as ContentElement['attributes']
        }
      )

    case 'delete-card-default-view-element-attribute':
      return updateCardElementAttributes<ContentElement>(
        prevState,
        {
          templateId: action.templateId,
          key: action.key,
          action: !!action.action
        },
        prevAttr => {
          return {
            ...del(prevAttr, action.attributePath)
          }
        }
      )

    case 'update-card-default-view-element-validations':
      return updateCardElementValidations<ContentElement>(
        prevState,
        {
          templateId: action.templateId,
          key: action.key,
          action: false
        },
        prevValidations => {
          return merge(prevValidations ?? {}, action.validations ?? {})
        }
      )

    case 'delete-card-default-view-element-validation':
      return updateCardElementValidations<ContentElement>(
        prevState,
        {
          templateId: action.templateId,
          key: action.key,
          action: false
        },
        prevValidations => {
          return {
            ...del(prevValidations, action.validationPath)
          }
        }
      )

    case 'update-card-default-view-action-attributes':
      return updateCardElementAttributes<ActionElement>(
        prevState,
        {
          templateId: action.templateId,
          key: action.key,
          action: true
        },
        prevAttr => {
          return merge(prevAttr, action.attributes)
        }
      )

    // In specific cases, when dealing with object input, don't merge
    case 'overwrite-card-default-view-action-attributes':
      return updateCardElementAttributes<ActionElement>(
        prevState,
        {
          templateId: action.templateId,
          key: action.key,
          action: true
        },
        prevAttr => {
          return overwrite<ActionElement>(prevAttr, action.attributes)
        }
      )

    case 'update-card-subview-element-attributes':
      return updateCardElementAttributes<InputElement>(
        prevState,
        {
          templateId: action.templateId,
          key: action.key,
          subviewId: action.subviewId,
          action: false
        },
        prevAttr => {
          return merge(prevAttr, action.attributes)
        }
      )

    // In specific cases, when dealing with object input, don't merge
    case 'overwrite-card-subview-element-attributes':
      return updateCardElementAttributes<InputElement>(
        prevState,
        {
          templateId: action.templateId,
          key: action.key,
          subviewId: action.subviewId,
          action: false
        },
        prevAttr => {
          return overwrite<InputElement>(prevAttr, action.attributes)
        }
      )

    case 'delete-card-subview-element-attribute':
      return updateCardElementAttributes<InputElement>(
        prevState,
        {
          templateId: action.templateId,
          key: action.key,
          subviewId: action.subviewId,
          action: !!action.action
        },
        prevAttr => {
          return {
            ...del(prevAttr, action.attributePath)
          }
        }
      )

    case 'update-card-subview-element-validations':
      return updateCardElementValidations<InputElement>(
        prevState,
        {
          templateId: action.templateId,
          key: action.key,
          subviewId: action.subviewId,
          action: false
        },
        prevValidations => {
          return merge(prevValidations ?? {}, action.validations ?? {})
        }
      )

    case 'delete-card-subview-element-validation':
      return updateCardElementValidations<InputElement>(
        prevState,
        {
          templateId: action.templateId,
          key: action.key,
          subviewId: action.subviewId,
          action: false
        },
        prevValidations => {
          return {
            ...del(prevValidations, action.validationPath)
          }
        }
      )

    case 'update-card-subview-action-attributes':
      return updateCardElementAttributes<ActionElement>(
        prevState,
        {
          templateId: action.templateId,
          key: action.key,
          subviewId: action.subviewId,
          action: true
        },
        prevAttr => {
          return merge(prevAttr, action.attributes)
        }
      )

    // In specific cases, when dealing with object input, don't merge
    case 'overwrite-card-subview-action-attributes':
      return updateCardElementAttributes<ActionElement>(
        prevState,
        {
          templateId: action.templateId,
          key: action.key,
          subviewId: action.subviewId,
          action: true
        },
        prevAttr => {
          return overwrite<ActionElement>(prevAttr, action.attributes)
        }
      )

    case 'delete-card-subview': {
      return updateCardTemplate(prevState, action.templateId, definition => ({
        ...definition,
        subviews: omit(definition.subviews, action.subviewId)
      }))
    }

    case 'update-card-element-order':
      return updateCardTemplate(prevState, action.templateId, definition => {
        const path = getElementPath({
          subviewId: action.subviewId,
          action: action.action
        })
        const existing = get<CardDefinition, UIElement[]>(definition, path)
        const sorted = action.keys.reduce<UIElement[]>((res, key) => {
          const found = existing.find(el => el.key === key)
          return found ? [...res, found] : res
        }, [])

        return set(definition, path, sorted)
      })

    case 'delete-card-element':
      return updateCardTemplate(prevState, action.templateId, definition => {
        const path = getElementPath({
          subviewId: action.subviewId,
          action: action.action
        })
        const existing = get<CardDefinition, UIElement[]>(definition, path)
        const updated = existing.filter(el => el.key !== action.key)

        return set(definition, path, updated)
      })

    case 'copy-card-element':
      return updateCardTemplate(prevState, action.templateId, definition => {
        const path = getElementPath({
          subviewId: action.subviewId,
          action: action.action
        })
        const existing = get<CardDefinition, UIElement[]>(definition, path)
        const existingNames = existing.map(el => get(el, 'attributes.name', ''))
        const updated = existing.reduce<UIElement[]>((res, el) => {
          if (el.key === action.keyToCopy) {
            let clone = deepClone(el)
            if ('name' in el.attributes) {
              // Not all elements have names ( only form fields as a rule )
              // If we've got a name update it so its unique as well
              const name = get(el, 'attributes.name')
              clone = set(
                clone,
                'attributes.name',
                copyName(name, existingNames)
              )
            }
            return [...res, el, {...clone, key: action.newKey}]
          }

          return [...res, el]
        }, [])

        return set(definition, path, updated)
      })

    case 'copy-card-template':
      return updateConfig(prevState, config => {
        const toCopy = config.cardTemplates?.[action.templateId]
        if (!toCopy) {
          return config
        }
        return {
          ...config,
          cardTemplates: {
            ...config.cardTemplates,
            [action.newCardId]: {
              ...toCopy,
              metadata: {
                ...toCopy.metadata,
                cardName: toCopy.metadata.cardName
                  ? copyName(
                      toCopy.metadata.cardName,
                      Object.values(config.cardTemplates ?? {}).map(
                        t => t.metadata.cardName ?? ''
                      )
                    )
                  : undefined
              }
            }
          }
        }
      })

    case 'update-connector-sample-payload':
      return updateConfig(prevState, state => {
        return {
          ...state,
          metadata: {
            ...state.metadata,
            payloadSamples: {
              step: {},
              ...state.metadata?.payloadSamples,
              connector: {
                ...state.metadata?.payloadSamples.connector,
                [action.connectorId]: {
                  sample: action.payload
                }
              }
            }
          }
        }
      })

    case 'update-step-sample-payload':
      return updateConfig(prevState, state => {
        return {
          ...state,
          metadata: {
            ...state.metadata,
            payloadSamples: {
              connector: {},
              ...state.metadata?.payloadSamples,
              step: {
                ...state.metadata?.payloadSamples.step,
                [action.stepId]: {
                  sample: action.sample
                }
              }
            }
          }
        }
      })
    case 'update-request-listener-mapping': {
      return updateRequestListener(prevState, action.triggerId, trigger => {
        if (trigger.type !== 'connector') {
          console.warn('Invalid step type')
        }
        return {
          ...trigger,
          properties: {
            ...trigger.properties,
            mapping: action.mapping
          }
        }
      })
    }

    case 'replace-config':
      return {...prevState, config: action.config}

    case 'update-card-notification-attributes': {
      return updateCardTemplate(prevState, action.cardTemplateId, update => {
        return {
          ...update,
          notification: {
            ...update.notification,
            attributes: merge(
              update.notification?.attributes ?? {},
              action.attributes
            )
          }
        }
      })
    }

    case 'update-card-notification': {
      return updateCardTemplate(prevState, action.cardTemplateId, update => {
        return {
          ...update,
          notification: action.notification
        }
      })
    }

    case 'remove-card-notification': {
      return updateCardTemplate(prevState, action.cardTemplateId, update => {
        return {
          ...update,
          notification: undefined
        }
      })
    }
    case 'update-card-delivery':
      return updateCardTemplate(prevState, action.templateId, template => {
        const {embargoUTCOffset, expiresUTCOffset, ...delivery} =
          action.delivery

        const result = {
          ...template,
          metadata: {
            ...template.metadata,
            delivery: merge(template.metadata?.delivery ?? {}, delivery),
            ...('embargoUTCOffset' in action.delivery
              ? {embargoUTCOffset}
              : {}),
            ...('expiresUTCOffset' in action.delivery ? {expiresUTCOffset} : {})
          }
        }

        return result
      })

    case 'add-context-mapping': {
      // Both card templates and steps can have context mappings
      // so we need to check which one we are updating
      return 'templateId' in action
        ? updateCardTemplate(prevState, action.templateId, template => {
            const result = {
              ...template,
              contextMapping: {
                ...(template.contextMapping ?? {}),
                [action.mappingId]: action.mapping
              }
            }
            return result
          })
        : updateStep(prevState, action.stepId, step => ({
            ...step,
            contextMapping: {
              ...('contextMapping' in step ? step.contextMapping : {}),
              [action.mappingId]: action.mapping
            }
          }))
    }

    case 'update-context-mapping': {
      // Both card templates and steps can have context mappings
      // so we need to check which one we are updating
      return 'templateId' in action
        ? updateCardTemplate(prevState, action.templateId, template => {
            const existing = template.contextMapping?.[action.mappingId]
            if (!existing) {
              return template
            }
            const result: CardDefinition = {
              ...template,
              contextMapping: {
                ...(template.contextMapping ?? {}),
                [action.mappingId]: merge(
                  existing,
                  action.mapping
                ) as ContextMappingValue
              }
            }
            return result
          })
        : updateStep(prevState, action.stepId, step => {
            if (step.type === 'start') {
              // appease TS, start steps don't have a context mapping property
              return step
            }
            const existing = step.contextMapping?.[action.mappingId]
            if (!existing) {
              return step
            }
            return {
              ...step,
              contextMapping: {
                ...step.contextMapping,
                [action.mappingId]: merge(
                  existing,
                  action.mapping
                ) as ContextMappingValue
              }
            }
          })
    }
    case 'update-menu-action': {
      return updateCardTemplate(prevState, action.templateId, template => {
        return {
          ...template,
          actions: merge(template.actions ?? defaultCardActions, {
            [action.action]: {
              [action.mode]: {
                enabled: action.enabled
              }
            }
          })
        }
      })
    }

    default: {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const unhandled: never = action

      return prevState
    }
  }
}
